import React, { useState, useMemo } from 'react';
import { 
    Box, 
    Table, 
    Thead, 
    Tbody, 
    Tr, 
    Th, 
    Td, 
    TableContainer, 
    Tag, 
    Text, 
    Icon,
    Flex,
    Tooltip as ChakraTooltip, 
    Button, 
    Select, 
    HStack,
    VStack,
    Input,
    InputGroup,
    InputLeftElement,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
} from '@chakra-ui/react';
import { 
    AiOutlineArrowDown, 
    AiOutlineArrowUp,
    AiOutlineLeft, 
    AiOutlineRight,
    AiOutlineSearch,
    AiOutlineDown
} from 'react-icons/ai';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const formatCurrency = (value) => {
    const numericValue = Number(value);
    if (isNaN(numericValue)) return 'R$ 0,00';
    return numericValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const CrossSellTable = ({ data = [] }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('todos'); // todos, em_dia, inadimplente
    const [sortConfig, setSortConfig] = useState({ key: 'vencimento', direction: 'asc' }); // Default sort by vencimento

    const filteredData = useMemo(() => {
        const lowerCaseSearch = searchTerm ? searchTerm.toLowerCase() : '';
        
        return data.filter(row => {
            const nomeCliente = (row.nome_FRANQUIA_MARCA_PROPRIA || row.franquia || row.marca || '').toLowerCase();
            const matchesSearch = !searchTerm || nomeCliente.includes(lowerCaseSearch);
            
            if (!matchesSearch) return false;

            const isVencido = Number(row.vencido) === 1;
            
            if (statusFilter === 'em_dia') {
                return !isVencido;
            }
            if (statusFilter === 'inadimplente') {
                return isVencido;
            }
            return true;
        });
    }, [data, searchTerm, statusFilter]);

    const sortedData = useMemo(() => {
        let sortableItems = [...filteredData];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue, bValue;

                // Mapeamento de chaves para os dados brutos
                switch (sortConfig.key) {
                    case 'cliente':
                        aValue = (a.nome_FRANQUIA_MARCA_PROPRIA || a.franquia || a.marca || '').toLowerCase();
                        bValue = (b.nome_FRANQUIA_MARCA_PROPRIA || b.franquia || b.marca || '').toLowerCase();
                        break;
                    case 'produto':
                        aValue = (a.descricao_FRANQUIA_MARCA_CUSTOS || '').toLowerCase();
                        bValue = (b.descricao_FRANQUIA_MARCA_CUSTOS || '').toLowerCase();
                        break;
                    case 'vencimento':
                        // data_vencimento_grid vem formatado dd/mm/yyyy ou com texto extra
                        // Idealmente usamos data_vencimento_FI_CONTAS_RECEBER que é a data real
                        aValue = a.data_vencimento_FI_CONTAS_RECEBER ? new Date(a.data_vencimento_FI_CONTAS_RECEBER) : new Date(0);
                        bValue = b.data_vencimento_FI_CONTAS_RECEBER ? new Date(b.data_vencimento_FI_CONTAS_RECEBER) : new Date(0);
                        break;
                    case 'valor':
                        aValue = Number(a.valorNogle || 0);
                        bValue = Number(b.valorNogle || 0);
                        break;
                    case 'status':
                        aValue = Number(a.vencido || 0);
                        bValue = Number(b.vencido || 0);
                        break;
                    default:
                        aValue = a[sortConfig.key];
                        bValue = b[sortConfig.key];
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [filteredData, sortConfig]);

    const totalItems = sortedData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    const paginatedData = useMemo(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
            const newStartIndex = (totalPages - 1) * itemsPerPage;
            return sortedData.slice(newStartIndex, newStartIndex + itemsPerPage);
        }
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, startIndex, endIndex, currentPage, totalPages, itemsPerPage]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <Icon as={AiOutlineArrowDown} opacity={0.2} ml={1} />;
        return sortConfig.direction === 'asc' 
            ? <Icon as={AiOutlineArrowUp} ml={1} color="green.400" /> 
            : <Icon as={AiOutlineArrowDown} ml={1} color="green.400" />;
    };

    const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
    const goToPrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
    
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1); 
    };
    
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); 
    };

    // --- Renderização de Status ---
    const renderStatusTag = (isInadimplente) => {
        if (isInadimplente) {
            return (
                <Tag size="sm" colorScheme="red" ml={2}>
                    Inadimplente
                </Tag>
            );
        }
        return (
            <Tag size="sm" colorScheme="green" ml={2}>
                Em Dia
            </Tag>
        );
    };
    
    if (!data || data.length === 0) {
        return (
            <Flex justify="center" align="center" minH="200px">
                <Text color="gray.500">Nenhuma oportunidade de cross-sell encontrada.</Text>
            </Flex>
        );
    }
    
    if (filteredData.length === 0) {
        return (
            <VStack spacing={4} align="stretch">
            <HStack spacing={4} w="100%">
                <InputGroup size="sm" maxW="300px">
                    <InputLeftElement pointerEvents="none">
                        <Icon as={AiOutlineSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input 
                        placeholder="Filtrar por nome do cliente..." 
                        value={searchTerm} 
                        onChange={handleSearchChange}
                        color="white"
                        variant="filled" 
                        bg="gray.700"
                        border="none"
                        focusBorderColor="transparent"
                        _hover={{ bg: 'gray.600' }}
                        _focus={{ bg: 'gray.600', boxShadow: 'none' }}
                        borderRadius="lg"
                        _placeholder={{ color: 'gray.500' }}
                    />
                </InputGroup>

                <Menu gutter={4}>
                    <MenuButton 
                        as={Button} 
                        rightIcon={<Icon as={AiOutlineDown} boxSize={3} />}
                        size="sm"
                        w="150px"
                        bg="gray.700"
                        color="white"
                        _hover={{ bg: 'gray.600' }}
                        _active={{ bg: 'gray.600' }}
                        textAlign="left"
                        fontWeight="normal"
                        borderRadius="lg"
                    >
                        {statusFilter === 'todos' ? 'Todos' : 
                         statusFilter === 'em_dia' ? 'Em Dia' : 'Inadimplentes'}
                    </MenuButton>
                    <MenuList 
                        bg="gray.800" 
                        borderColor="gray.900" 
                        borderRadius="xl"
                        p={1}
                        boxShadow="dark-lg"
                        minW="150px"
                        w="150px"
                    >
                        <MenuItem 
                            onClick={() => setStatusFilter('todos')}
                            bg="transparent"
                            _hover={{ bg: 'gray.700' }}
                            color="white"
                            borderRadius="md"
                        >
                            Todos
                        </MenuItem>
                        <MenuItem 
                            onClick={() => setStatusFilter('em_dia')}
                            bg="transparent"
                            _hover={{ bg: 'gray.700' }}
                            color="white"
                            borderRadius="md"
                        >
                            Em Dia
                        </MenuItem>
                        <MenuItem 
                            onClick={() => setStatusFilter('inadimplente')}
                            bg="transparent"
                            _hover={{ bg: 'gray.700' }}
                            color="white"
                            borderRadius="md"
                        >
                            Inadimplentes
                        </MenuItem>
                    </MenuList>
                </Menu>
            </HStack>
                <Flex justify="center" align="center" minH="200px">
                    <Text color="gray.500">Nenhum resultado encontrado para "{searchTerm}".</Text>
                </Flex>
            </VStack>
        );
    }
    
    if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(totalPages);
    }

    return (
        <VStack spacing={4} align="stretch">
            
            <HStack spacing={4} w="100%">
                <InputGroup size="sm" maxW="300px">
                    <InputLeftElement pointerEvents="none">
                        <Icon as={AiOutlineSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input 
                        placeholder="Filtrar por nome do cliente..." 
                        value={searchTerm} 
                        onChange={handleSearchChange}
                        color="white"
                        variant="filled" 
                        bg="gray.700"
                        border="none"
                        focusBorderColor="transparent"
                        _hover={{ bg: 'gray.600' }}
                        _focus={{ bg: 'gray.600', boxShadow: 'none' }}
                        borderRadius="lg"
                        _placeholder={{ color: 'gray.500' }}
                    />
                </InputGroup>

                <Menu gutter={4}>
                    <MenuButton 
                        as={Button} 
                        rightIcon={<Icon as={AiOutlineDown} boxSize={3} />}
                        size="sm"
                        w="150px"
                        bg="gray.700"
                        color="white"
                        _hover={{ bg: 'gray.600' }}
                        _active={{ bg: 'gray.600' }}
                        textAlign="left"
                        fontWeight="normal"
                        borderRadius="lg"
                    >
                        {statusFilter === 'todos' ? 'Todos' : 
                         statusFilter === 'em_dia' ? 'Em Dia' : 'Inadimplentes'}
                    </MenuButton>
                    <MenuList 
                        bg="gray.800" 
                        borderColor="gray.900" 
                        borderRadius="xl"
                        p={1}
                        boxShadow="dark-lg"
                        minW="150px"
                        w="150px"
                    >
                        <MenuItem 
                            onClick={() => setStatusFilter('todos')}
                            bg="transparent"
                            _hover={{ bg: 'gray.700' }}
                            color="white"
                            borderRadius="md"
                        >
                            Todos
                        </MenuItem>
                        <MenuItem 
                            onClick={() => setStatusFilter('em_dia')}
                            bg="transparent"
                            _hover={{ bg: 'gray.700' }}
                            color="white"
                            borderRadius="md"
                        >
                            Em Dia
                        </MenuItem>
                        <MenuItem 
                            onClick={() => setStatusFilter('inadimplente')}
                            bg="transparent"
                            _hover={{ bg: 'gray.700' }}
                            color="white"
                            borderRadius="md"
                        >
                            Inadimplentes
                        </MenuItem>
                    </MenuList>
                </Menu>
            </HStack>

            <TableContainer>
                <Table variant="simple" size="sm">
                    <Thead>
                        <Tr>
                            <Th 
                                color="gray.400" 
                                cursor="pointer" 
                                onClick={() => requestSort('cliente')}
                                _hover={{ color: 'white' }}
                            >
                                Cliente {getSortIcon('cliente')}
                            </Th> 
                            <Th 
                                color="gray.400" 
                                textAlign="center"
                                cursor="pointer" 
                                onClick={() => requestSort('produto')}
                                _hover={{ color: 'white' }}
                            >
                                Produto {getSortIcon('produto')}
                            </Th> 
                            <Th 
                                color="gray.400" 
                                textAlign="center"
                                cursor="pointer" 
                                onClick={() => requestSort('vencimento')}
                                _hover={{ color: 'white' }}
                            >
                                Vencimento {getSortIcon('vencimento')}
                            </Th> 
                            <Th 
                                color="gray.400" 
                                isNumeric
                                cursor="pointer" 
                                onClick={() => requestSort('valor')}
                                _hover={{ color: 'white' }}
                            >
                                Valor {getSortIcon('valor')}
                            </Th> 
                            <Th 
                                color="gray.400" 
                                textAlign="center"
                                cursor="pointer" 
                                onClick={() => requestSort('status')}
                                _hover={{ color: 'white' }}
                            >
                                Status {getSortIcon('status')}
                            </Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {paginatedData.map((row, index) => {
                            const nomeCliente = row.nome_FRANQUIA_MARCA_PROPRIA || row.franquia || row.marca || 'N/D';
                            const produto = row.descricao_FRANQUIA_MARCA_CUSTOS || 'N/D';
                            const vencimento = row.data_vencimento_grid || 'N/D';
                            const valor = row.valorNogle || 0;
                            const isVencido = Number(row.vencido) === 1;

                            return (
                                <Tr key={index} _hover={{ bg: 'gray.800' }}>
                                    <Td color="white">
                                        {nomeCliente}
                                    </Td>
                                    
                                    <Td color="gray.300" fontSize="sm" textAlign="center">
                                        {produto}
                                    </Td>

                                    <Td color="gray.300" fontSize="xs" textAlign="center">
                                        {vencimento}
                                    </Td>

                                    <Td isNumeric color="white" fontWeight="medium">
                                        {formatCurrency(valor)}
                                    </Td>

                                    <Td color="gray.300" textAlign="center">
                                        {renderStatusTag(isVencido)}
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </TableContainer>

            <Flex justify="space-between" align="center" pt={2} px={2} w="100%">
                <HStack spacing={2} align="center">
                    <Text fontSize="sm" color="gray.400">Mostrar:</Text>
                    <Menu gutter={4} placement="bottom">
                        <MenuButton 
                            as={Button} 
                            rightIcon={<Icon as={AiOutlineDown} boxSize={3} />}
                            size="sm"
                            w="80px"
                            bg="gray.700"
                            color="white"
                            _hover={{ bg: 'gray.600' }}
                            _active={{ bg: 'gray.600' }}
                            textAlign="left"
                            fontWeight="normal"
                            borderRadius="lg"
                        >
                            {itemsPerPage}
                        </MenuButton>
                        <MenuList 
                            bg="gray.800" 
                            borderColor="gray.900" 
                            borderRadius="xl"
                            p={1}
                            boxShadow="dark-lg"
                            minW="80px"
                            w="80px"
                        >
                            {ITEMS_PER_PAGE_OPTIONS.map(option => (
                                <MenuItem 
                                    key={option} 
                                    onClick={() => handleItemsPerPageChange({ target: { value: option } })}
                                    bg="transparent"
                                    _hover={{ bg: 'gray.700' }}
                                    color="white"
                                    borderRadius="md"
                                >
                                    {option}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Menu>
                    <Text fontSize="sm" color="gray.400">por página</Text>
                </HStack>

                <HStack spacing={4} align="center">
                    <Text fontSize="sm" color="gray.400" whiteSpace="nowrap">
                        Exibindo {Math.min(startIndex + 1, totalItems)} - {Math.min(endIndex, totalItems)} de {totalItems} resultados
                    </Text>
                    <Button 
                        onClick={goToPrevPage} 
                        isDisabled={currentPage === 1 || totalPages === 0} 
                        size="sm" 
                        variant="ghost" 
                        color="gray.400" 
                        _hover={{ bg: 'gray.700', color: 'white' }}
                        leftIcon={<Icon as={AiOutlineLeft} />}
                    >
                        Anterior
                    </Button>
                    <Button 
                        onClick={goToNextPage} 
                        isDisabled={currentPage === totalPages || totalPages === 0} 
                        size="sm" 
                        variant="ghost" 
                        color="gray.400" 
                        _hover={{ bg: 'gray.700', color: 'white' }}
                        rightIcon={<Icon as={AiOutlineRight} />}
                    >
                        Próxima
                    </Button>
                </HStack>
            </Flex>
        </VStack>
    );
};

export default CrossSellTable;